import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactLabel } from 'src/components/domain/contacts/contact-label';
import { ContactOptionsBottomDrawer } from 'src/components/domain/contacts/contact-options-bottom-drawer';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  baseListMetaStyles,
  baseListStormStyles,
  baseListContainerStyles,
  baseEllipsisStyles,
  baseListItemStyles,
  DashboardContactStageIcon,
  DashboardAssigneeIcon,
} from 'src/components/pages/dashboard/components/cards/shared/base-list';
import { CONTACT_LABEL_PREVIEW_FIELDS } from 'src/components/pages/dashboard/components/cards/shared/helpers';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  CardBaseContent,
  CardListScrollOverlayIndicator,
  DashboardCard,
  emptyValuePlaceholder,
} from 'src/components/pages/dashboard/components/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useVisible } from 'src/hooks/use-visible';

const StyledCard = styled(DashboardCard)`
  width: 100%;
`;

const ItemsList = styled(Scrollbar)`
  ${baseListContainerStyles};
  height: 311px;
  gap: 15px;
  padding-bottom: 25px;
`;

const ItemContainer = styled(FlexRow)`
  ${baseListItemStyles};
  align-items: center;
  gap: 10px;
  height: 45px;
  justify-content: space-between;
  padding-bottom: 15px;
`;

const ContactContainer = styled(FlexRow)`
  gap: 10px;
  overflow: hidden;
  width: 100%;
`;

const ContactDetails = styled(FlexColumn)`
  overflow: hidden;
  width: 100%;
`;

const StyledContactLabel = styled(ContactLabel)`
  line-height: 15px;
  width: 100%;

  .contact-label_text {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 13px;
    font-style: normal;
    line-height: 15px;
  }

  .contact-label-text-column {
    &,
    a {
      width: 100%;
    }
  }
`;

const AssigneeContainer = styled(FlexRow)`
  ${baseListStormStyles};
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const AssigneeName = styled.span`
  ${baseListMetaStyles}
  ${baseEllipsisStyles}
`;

const DateContainer = styled(FlexColumn)`
  align-items: flex-end;
  gap: 1px;
`;

const DateLabel = styled.span`
  ${baseListStormStyles}
`;

const DateValue = styled.span`
  ${baseListMetaStyles}
  ${baseEllipsisStyles}
`;

export const GET_ASSIGNED_CONTACTS_QUERY = gql`
  query GetAssignedContacts($institutionId: Int!, $where: ContactWhereInput) {
    contacts(
      first: 10
      institutionId: $institutionId
      where: $where
      orderBy: { assignedAt: DESC }
    ) {
      totalCount
      edges {
        node {
          ...ContactLabelPreviewFields
          assignedAt
          assignee {
            id
            fullName
          }
        }
      }
    }
  }
  ${CONTACT_LABEL_PREVIEW_FIELDS}
`;

const CardContentContainer = styled(CardBaseContent)`
  height: 311px;
`;

const EmptyCardContentContainer = styled(CardBaseContent)`
  height: 357px;
`;

type AssignedContactListItemProps = {
  contact: Contact;
  testId: string;
};

const AssignedContactListItem = ({ contact, testId }: AssignedContactListItemProps) => {
  const { t } = useTranslation(['dashboard']);
  const { id, stage, label, assignee, assignedAt } = contact;
  const { formatDueDate } = useFormatDate();
  const optionsBottomDrawerVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));

  return (
    <>
      <ItemContainer
        key={id}
        onClick={!breakpointUpMd ? optionsBottomDrawerVisibilityHandler.show : undefined}
      >
        <ContactContainer>
          <DashboardContactStageIcon slug={stage.slug} name={label} variant="large" />
          <ContactDetails>
            <StyledContactLabel
              testId={`${testId}-contact-${id}`}
              contact={contact}
              showStageIcon={false}
            />
            <AssigneeContainer>
              <DashboardAssigneeIcon type="assignedto" />
              <AssigneeName
                data-lgg-id={`${testId}-contact-${id}-assignee-${
                  assignee?.id ?? 'unassigned'
                }`}
              >
                {assignee?.fullName ?? emptyValuePlaceholder}
              </AssigneeName>
            </AssigneeContainer>
          </ContactDetails>
        </ContactContainer>
        <DateContainer>
          <DateLabel>{t('dashboard:cards.assignedContacts.assignedAt')}</DateLabel>
          <DateValue data-lgg-id={`${testId}-contact-${id}-date`}>
            {assignedAt ? formatDueDate(assignedAt) : emptyValuePlaceholder}
          </DateValue>
        </DateContainer>
      </ItemContainer>
      {!breakpointUpMd ? (
        <ContactOptionsBottomDrawer
          visible={optionsBottomDrawerVisibilityHandler.visible}
          onClose={optionsBottomDrawerVisibilityHandler.close}
          contact={contact}
        />
      ) : null}
    </>
  );
};

export const AssignedContactsCard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const currentInstitution = useCurrentInstitution();
  const { targetUserId } = useContext(DashboardFiltersContext);

  const handleGraphQLError = useHandleGraphQLError();

  const { data, loading } = useQuery(GET_ASSIGNED_CONTACTS_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      where: {
        assignee: targetUserId ? { id: { _eq: targetUserId } } : { _ne: null },
      },
    },
    onError: handleGraphQLError,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const queryParams = useMemo(() => {
    const params = {
      institution_id: currentInstitution.id,
      institution_type: 'company',
      'view-code': 'custom',
      'q[advanced][assigned_unassigned]': '0',
      table: 'sort|assignedAt|direction|DESC',
      ...(targetUserId && { 'q[advanced][agent]': targetUserId }),
    };

    return Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }, [currentInstitution.id, targetUserId]);

  const contacts = data?.contacts?.edges ?? [];
  const isEmpty = !loading && contacts.length === 0;
  const testId = 'assigned-contacts-card';

  const cardTitle = t('dashboard:cards.assignedContacts.title');

  return (
    <StyledCard loading={loading} data-lgg-id={testId}>
      <DashboardCard.Header
        testId={`${testId}-header`}
        title={cardTitle}
        cardInfoI18nKey="dashboard:cards.assignedContacts.tooltipDescription"
        cardTextTitle={cardTitle}
      />
      <DashboardCard.Content data-lgg-id={`${testId}-content`}>
        {isEmpty ? (
          <EmptyCardContentContainer $isEmpty>
            <DashboardCard.EmptyState
              testId="assigned-contacts-card-empty"
              icon="contact"
              title={t('dashboard:cards.assignedContacts.noAssignedContacts')}
            />
          </EmptyCardContentContainer>
        ) : (
          <CardContentContainer>
            <CardListScrollOverlayIndicator $showBottomShadow>
              <ItemsList>
                {contacts.map(({ node }) => (
                  <AssignedContactListItem contact={node} testId={testId} key={node.id} />
                ))}
              </ItemsList>
            </CardListScrollOverlayIndicator>
          </CardContentContainer>
        )}
      </DashboardCard.Content>
      {!isEmpty && (
        <DashboardCard.Footer
          testId={`${testId}-footer`}
          linkTo={`/i/company/${currentInstitution.id}/contacts?${queryParams}`}
          linkText={t('common:viewMore')}
        />
      )}
    </StyledCard>
  );
};
